import React, { useContext, useState, useEffect, useMemo } from 'react';
import Api from 'api';
import { FormGrid, FormField, FormRow, FormColumn, FormSubmit } from 'components/forms';
import { Input, SelectInput } from 'components/UI';
import withForm from 'components/forms/withForm';
import { ErrorsShakeHubContext, findFirstFieldWithError } from 'components/forms/utils';
import { contactFormSchema } from 'components/forms/validationSchemas';
import { CaptchaInfo } from '@landing/ui';
import { pushCustomSubmitEvent } from 'utils';
import { getCountryCode } from 'utils/localised';
import { phoneCode } from 'define/locale';
import { eventsData } from 'define';

import * as styles from './Form.module.scss';

const InnerForm = ({
  handleSubmit,
  fields,
  isValid,
  values,
  errors,
  isSubmitting,
  status,
  setStatus,
  formName,
  id,
  className,
  captchaInfo,
}) => {
  const errorsShakeHub = useContext(ErrorsShakeHubContext);
  const [btnPressed, setBtnPressed] = useState(false);

  const preparedEvents = useMemo(() => {
    const options = fields.event.options.filter(
      ({ value }) => new Date(eventsData[value].date) >= new Date(),
    );

    return {
      ...fields.event,
      options,
    };
  }, [fields.event]);

  useEffect(() => {
    if (!isValid && btnPressed) {
      errorsShakeHub.dispatchEvent(new CustomEvent('shakeError'));
      errorsShakeHub.dispatchEvent(
        new CustomEvent('setFocus', {
          detail: findFirstFieldWithError(values, errors),
        }),
      );
      setBtnPressed(false);
    }
  }, [isValid, btnPressed, errorsShakeHub, values, errors]);

  useEffect(() => {
    if (status === 'success') {
      setStatus();
    }
  }, [status, setStatus]);

  return (
    <FormGrid
      submitHandler={e => {
        setBtnPressed(true);
        handleSubmit(e);
      }}
      formName={formName}
      id={id}
      className={className}
    >
      <FormRow sizes={['sm12']}>
        <FormColumn className={styles.column}>
          <FormField Component={SelectInput} {...preparedEvents} />
          <FormField Component={Input} {...fields.name} />
          <FormField Component={Input} {...fields.phone} />
          <FormField Component={Input} {...fields.email} />
        </FormColumn>
      </FormRow>
      <div className={styles.submitWrap}>
        <FormSubmit title="Send Invitation" isSubmitting={isSubmitting} />
      </div>
      {captchaInfo && (
        <CaptchaInfo className={styles.captcha} additionalText={captchaInfo} theme="owlSoul" />
      )}
    </FormGrid>
  );
};

export default function Form({
  id = 'owl-soul-exhibition',
  formName = 'owl-soul-exhibition',
  closeModal,
  onSuccess,
  additionalFormData,
  className,
  getAdditionalFormData,
  inModal = false,
  captchaInfo,
  initialEvent,
}) {
  const fields = {
    event: {
      initialValue: initialEvent ?? '',
      options: [
        {
          content: 'Exhibition Opening',
          value: 'Exhibition Opening',
        },
        {
          content: 'Art and Fashion: Luxury brands inspired by classical art',
          value: 'Art and Fashion: Luxury brands inspired by classical art',
        },
        {
          content: 'Contemporary Art in Perfume',
          value: 'Contemporary Art in Perfume',
        },
        {
          content: 'Drinking Art and Art of Drinking',
          value: 'Drinking Art and Art of Drinking',
        },
        {
          content: 'Chef’s Table - Art of Cooking',
          value: 'Chef’s Table - Art of Cooking',
        },
      ],
      placeholder: 'Select Event',
      middleware: ({ formik, options, ...rest }) => {
        if (formik.values.event) {
          const event = options.find(event => event.value === formik.values.event);
          const eventDate = new Date(eventsData[event.value].date).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            timeZone: 'Europe/Warsaw',
          });
          formik.values.eventDate = eventDate;
          formik.values.eventTime = eventsData[event.value].time;
        }
        return { formik, options, ...rest };
      },
    },
    name: {
      initialValue: '',
      domProps: {
        placeholder: 'Name',
        maxLength: 500,
      },
    },
    phone: {
      initialValue: '',
      getInitialValue,
      mask: {
        mask: '+99999999999999999',
        maskChar: '',
        formatChars: {
          9: '[0-9]',
        },
      },
      domProps: {
        placeholder: 'Phone Number',
        type: 'tel',
      },
    },
    email: {
      initialValue: '',
      domProps: {
        placeholder: 'Email',
        type: 'email',
      },
    },
  };

  const errorsMessages = {
    event: 'Please, select an Event',
    name: 'Please, enter your name',
    phone: 'Please, enter your phone number',
    email: 'Please, enter your email',
  };

  const Component = withForm({
    fields,
    formName,
    id,
    className,
    captchaInfo,
    validation: props => contactFormSchema(errorsMessages, props),
    submit: {
      id,
      formName,
      handler: Api.sendForm.bind(Api, 'integration/form-data'),
      reset: true,
      successModal: 'SuccessModal',
      errorModal: 'ErrorModal',
      onSuccess,
      additionalFormData,
      getAdditionalFormData,
      pushSubmitEvent: () => pushCustomSubmitEvent(id),
      onResponse: () => {
        if (inModal) {
          closeModal();
        }
      },
    },
  })(InnerForm);

  return <Component />;
}

function getInitialValue() {
  if (typeof window !== 'undefined') {
    const country = getCountryCode()?.toLowerCase();
    return phoneCode[country] ?? '+';
  }
  return '+';
}
